/**
 * Route tables of the application: most specific first, "not found" last. A new page is a
 * component in pages/ plus an entry here; the shell, the router and the navigation read these
 * lists and nothing else.
 *
 * Two tables, chosen by the shell once it knows who signed in: `routes` for operators, the
 * console of servers, revisions and applies; `customerRoutes` for customer users, the pages of
 * pages/customer/, which speak of workspaces and their assistants, never of servers or revisions.
 */
import { html } from 'lit';

import './pages/overview-page.js';
import './pages/servers-page.js';
import './pages/server-page.js';
import './pages/instances-page.js';
import './pages/instance-page.js';
import './pages/new-instance-page.js';
import './pages/revision-page.js';
import './pages/revision-editor-page.js';
import './pages/customers-page.js';
import './pages/customer-page.js';
import './pages/new-customer-page.js';
import './pages/templates-page.js';
import './pages/template-editor-page.js';
import './pages/not-found-page.js';
import './pages/customer/home-page.js';
import './pages/customer/workspace-page.js';
import './pages/customer/assistant-editor-page.js';
import './pages/customer/telegram-page.js';
import './pages/customer/members-page.js';

export const routes = [
  {
    path: '/',
    title: 'Overview',
    render: () => html`<plane-overview-page></plane-overview-page>`,
  },
  {
    path: '/servers',
    title: 'Servers',
    render: () => html`<plane-servers-page></plane-servers-page>`,
  },
  {
    path: '/servers/:serverId',
    title: (params) => params.serverId,
    render: (params) => html`<plane-server-page .serverId=${params.serverId}></plane-server-page>`,
  },
  {
    path: '/workspaces',
    title: 'Workspaces',
    render: () => html`<plane-instances-page></plane-instances-page>`,
  },
  {
    // ?server=<id>: the server it runs on, chosen already
    path: '/workspaces/new',
    title: 'New workspace',
    render: () => html`<plane-new-instance-page></plane-new-instance-page>`,
  },
  {
    path: '/workspaces/:name',
    title: (params) => params.name,
    render: (params) => html`<plane-instance-page .name=${params.name}></plane-instance-page>`,
  },
  {
    // ?from=<revisionId>: the revision the new one starts from; the desired one otherwise
    path: '/workspaces/:name/edit',
    title: (params) => `Edit ${params.name}`,
    render: (params) => html`<plane-revision-editor-page .name=${params.name}></plane-revision-editor-page>`,
  },
  {
    path: '/workspaces/:name/revisions/:revisionId',
    title: (params) => `${params.name} · revision`,
    render: (params) => html`<plane-revision-page .name=${params.name} .revisionId=${params.revisionId}></plane-revision-page>`,
  },
  {
    // the addresses of before workspaces, bookmarked
    path: '/instances',
    title: 'Workspaces',
    render: () => html`<plane-instances-page></plane-instances-page>`,
  },
  {
    path: '/instances/:name',
    title: (params) => params.name,
    render: (params) => html`<plane-instance-page .name=${params.name}></plane-instance-page>`,
  },
  {
    path: '/customers',
    title: 'Customers',
    render: () => html`<plane-customers-page></plane-customers-page>`,
  },
  {
    path: '/customers/new',
    title: 'New customer',
    render: () => html`<plane-new-customer-page></plane-new-customer-page>`,
  },
  {
    path: '/customers/:customerId',
    title: 'Customer',
    render: (params) => html`<plane-customer-page .customerId=${params.customerId}></plane-customer-page>`,
  },
  {
    path: '/templates',
    title: 'Templates',
    render: () => html`<plane-templates-page></plane-templates-page>`,
  },
  {
    // ?fromInstance=<ref>: a copy of the desired revision of that instance
    path: '/templates/new',
    title: 'New template',
    render: () => html`<plane-template-editor-page></plane-template-editor-page>`,
  },
  {
    path: '/templates/:templateId',
    title: 'Template',
    render: (params) => html`<plane-template-editor-page .templateId=${params.templateId}></plane-template-editor-page>`,
  },
  {
    title: 'Not found',
    render: () => html`<plane-not-found-page></plane-not-found-page>`,
  },
];

export const customerRoutes = [
  {
    path: '/',
    title: 'Your workspaces',
    render: () => html`<plane-customer-home-page></plane-customer-home-page>`,
  },
  {
    path: '/workspaces/:name',
    title: 'Your workspace',
    render: (params) => html`<plane-workspace-page .name=${params.name}></plane-workspace-page>`,
  },
  {
    path: '/workspaces/:name/edit',
    title: 'Edit assistant',
    render: (params) => html`<plane-assistant-editor-page .name=${params.name}></plane-assistant-editor-page>`,
  },
  {
    // one assistant of a workspace with several
    path: '/workspaces/:name/assistants/:assistant/edit',
    title: 'Edit assistant',
    render: (params) => html`<plane-assistant-editor-page .name=${params.name} .assistant=${params.assistant}></plane-assistant-editor-page>`,
  },
  {
    path: '/workspaces/:name/assistants/:assistant/telegram',
    title: 'Connect Telegram',
    render: (params) => html`<plane-telegram-page .name=${params.name} .assistant=${params.assistant}></plane-telegram-page>`,
  },
  {
    path: '/workspaces/:name/telegram',
    title: 'Connect Telegram',
    render: (params) => html`<plane-telegram-page .name=${params.name}></plane-telegram-page>`,
  },
  {
    path: '/people',
    title: 'People',
    render: () => html`<plane-customer-members-page></plane-customer-members-page>`,
  },
  {
    // a link to the operator's page of a workspace, shared or bookmarked: the same workspace
    path: '/instances/:name',
    title: 'Your workspace',
    render: (params) => html`<plane-workspace-page .name=${params.name}></plane-workspace-page>`,
  },
  {
    // the address of a workspace page before workspaces had their name, bookmarked
    path: '/assistants/:name',
    title: 'Your workspace',
    render: (params) => html`<plane-workspace-page .name=${params.name}></plane-workspace-page>`,
  },
  {
    title: 'Not found',
    render: () => html`<plane-not-found-page></plane-not-found-page>`,
  },
];

/**
 * Entries of the main navigation, the most used first: a link is active on its path and below.
 * `operator` entries are shown to operators only (the backend answers 403 to anybody else anyway).
 * `pending` names a count the shell shows on the link when it is not zero: something waits there.
 */
export const navigation = [
  { href: '/', label: 'Overview' },
  { href: '/workspaces', label: 'Workspaces' },
  { href: '/customers', label: 'Customers', operator: true },
  { href: '/templates', label: 'Templates', operator: true },
  // infrastructure, set up once and visited when something breaks: last, with the clawlets waiting
  { href: '/servers', label: 'Servers', operator: true, pending: 'enrollmentRequests' },
];
